import { StreamingStatus } from '@heidi/session/contracts';
import { get } from 'lodash-es';
import type posthog from 'posthog-js';

import type { EffectiveSessionTimeout } from '@heidi/workspace/contracts';

/**
 * Statuses that represent an in-flight note generation, during which idle
 * logout must be suppressed.
 *
 * `undefined` (no session open — the session query only runs on session
 * routes) and the settled statuses (`NOT_STARTED`, `MANUALLY_ABORTED`,
 * `GENERATION_FAILED`) must NOT block logout: a
 * `status !== NOT_STARTED` comparison treats `undefined` as "generating",
 * which keeps the guard permanently true on every non-session page, so the
 * inactivity timeout never fires anywhere except an open, loaded session.
 */
const IN_FLIGHT_STREAMING_STATUSES: ReadonlySet<StreamingStatus> = new Set([
  StreamingStatus.AWAITING_STREAMING_START,
  StreamingStatus.STREAMING,
  StreamingStatus.UPLOADING,
  StreamingStatus.FINISHING_STREAMING,
  StreamingStatus.CONVERTING_SECTIONAL_NOTES,
]);

export function isGenerationInFlight(
  streamingStatus: StreamingStatus | undefined
): boolean {
  return (
    streamingStatus !== undefined && IN_FLIGHT_STREAMING_STATUSES.has(streamingStatus)
  );
}

/**
 * Resolve the effective session-inactivity timeout (in minutes) by preferring
 * the back-end-resolved value with a PostHog fallback during the rollout.
 *
 * Rollout (MUL-1879):
 * - PR-5 (this code): BE preferred, PostHog fallback retained for safety —
 *   if the Flagsmith cohort isn't enabled yet for a user, BE returns
 *   `session_timeout: null` and we fall back to the legacy PostHog flag.
 * - PR-8 (later): drop the PostHog fallback once Flagsmith cohort coverage
 *   matches the PostHog flag's current rollout.
 *
 * Branch semantics:
 * - **BE branch**: returns `beTimeout.minutes` when present AND `> 0`. The
 *   BE service layer enforces the `[1, 720]` bounds, so in normal operation
 *   a non-positive value would never arrive here. The `> 0` check is
 *   defence-in-depth against a buggy or schema-drifted BE response: a `0`
 *   would mount `<AutoLogout timeToLogout={0} />` and call `useIdle(0)`,
 *   firing logout on every render; a negative value is browser-clamped to
 *   `0` and has the same effect. Falling through to PostHog on a bad BE
 *   value is the safer degrade. This does not compromise the design's
 *   "cannot be disabled within cohort" guarantee — users still cannot
 *   deliberately set `0`, because the BE rejects it at the write path.
 * - **PostHog branch**: retains the legacy `> 0 = disabled` semantics
 *   until the fallback is removed in PR-8. A `0` or `null` PostHog payload
 *   means "no timeout".
 *
 * Returns `null` to signal "do not mount the idle-logout component".
 */
export function resolveTimeToLogout(
  beTimeout: EffectiveSessionTimeout | null | undefined,
  posthogClient: Pick<typeof posthog, 'getFeatureFlagPayload'>
): number | null {
  if (typeof beTimeout?.minutes === 'number' && beTimeout.minutes > 0) {
    return beTimeout.minutes;
  }
  const posthogPayload = posthogClient.getFeatureFlagPayload(
    'release_inactivity_logout_in_minutes'
  );
  const posthogMinutes = get(posthogPayload, 'minutes', null);
  if (typeof posthogMinutes === 'number' && posthogMinutes > 0) {
    return posthogMinutes;
  }
  return null;
}
